import PropTypes from 'prop-types';
import React, { useState } from 'react';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Table } from '../../components/widgets';
import { getAdminModeEnabled, getUserData } from '../../selectors/user';
import {
    ADMIN_EDITABLE_STATUSES,
    DISABLED_REASONS,
    INTEGRATION_CARDS,
    READ_ONLY_STATUSES,
    REQUEST_TYPES,
    STATUSES,
    TRANSLATE_PREFIX,
} from '../consts';
import {
    downloadAttributionRequestCsv,
    formatDate,
    getIntegrationTableMetadata,
    getStatusForType,
    isFormType,
} from '../utils';
import css from './IntegrationTable.css';

const BASE_PATH = '/react/integrations';

const getDisabledState = ({ profileIncomplete }) => {
    if (profileIncomplete) {
        return { disabled: true, disabledReason: DISABLED_REASONS.PROFILE_INCOMPLETE };
    }

    return { disabled: false, disabledReason: null };
};

const IntegrationTable = ({ requests, hiddenByType, profileIncomplete, onRequestIntegration, onDeleteClick }) => {
    const navigate = useNavigate();
    const adminMode = useSelector(getAdminModeEnabled);
    const orgName = useSelector(state => getUserData(state).company);
    const [expandedTypes, setExpandedTypes] = useState({});
    const [loadingTypes, setLoadingTypes] = useState({});

    const toggleExpanded = type => {
        setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const handleRequestIntegration = card => {
        setLoadingTypes(prev => ({ ...prev, [card.type]: true }));
        Promise.resolve(onRequestIntegration(card)).finally(() => {
            setLoadingTypes(prev => ({ ...prev, [card.type]: false }));
        });
    };

    const navigateToType = type => {
        navigate(`${BASE_PATH}?type=${encodeURIComponent(type)}`);
    };

    const navigateToRequest = requestId => {
        navigate(`${BASE_PATH}?request_id=${requestId}`);
    };

    const tableMetadata = getIntegrationTableMetadata({
        translatePrefix: TRANSLATE_PREFIX,
        adminMode,
    });

    const tableData = INTEGRATION_CARDS.flatMap(card => {
        const { status, request } = getStatusForType(requests, card.type);
        const { disabled, disabledReason } = getDisabledState({ profileIncomplete });
        const isReadOnly = READ_ONLY_STATUSES.includes(status);
        const isFormRequest = isFormType(card.type);
        const isDraft = status === STATUSES.DRAFT;
        const isNotStarted = status === STATUSES.NOT_STARTED;
        const hiddenRequests = hiddenByType[card.type] || [];
        const hasHidden = adminMode && hiddenRequests.length > 0;
        const isExpanded = !!expandedTypes[card.type];

        const buildRow = (req, overrides = {}) => ({
            card,
            integration: card.type,
            status: req?.status ?? status,
            request: req,
            disabled: false,
            disabledReason: null,
            lastEdited: formatDate(req?.updated_at),
            submittedAt: formatDate(req?.submitted_at),
            reviewer: req?.reviewer || null,
            reviewerComments: req?.reviewer_comments || null,
            ...overrides,
        });

        const mainRow = buildRow(request, {
            key: card.type,
            status,
            disabled,
            disabledReason,
            trClass: disabled ? css.disabledRow : '',
            actions: (
                <div className={css.actions} onClick={e => e.stopPropagation()}>
                    {isNotStarted &&
                        (isFormRequest ? (
                            <Button disabled={disabled} type="link" onClick={() => navigateToType(card.type)}>
                                <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.START`} /> ›
                            </Button>
                        ) : (
                            <Button
                                disabled={disabled || loadingTypes[card.type]}
                                showSpinner={!!loadingTypes[card.type]}
                                type="flat"
                                Icon="mail"
                                onClick={() => handleRequestIntegration(card)}
                            >
                                <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.REQUEST_INTEGRATION`} />
                            </Button>
                        ))}
                    {isDraft && (
                        <>
                            <Button
                                disabled={disabled}
                                type="flat"
                                Icon="pencil"
                                tooltipKey="STATIC.BUTTONS.EDIT"
                                className={classNames(css.iconButton, css.rounded)}
                                onClick={() => navigateToRequest(request?.id)}
                            />
                            <Button
                                disabled={disabled}
                                type="flat"
                                Icon="trash"
                                tooltipKey="STATIC.BUTTONS.DELETE"
                                className={classNames(css.iconButton, css.rounded, css.deleteButton)}
                                onClick={e => onDeleteClick(e, request)}
                            />
                        </>
                    )}
                    {status === STATUSES.APPROVED && card.type === REQUEST_TYPES.ATTRIBUTION && (
                        <Button
                            type="flat"
                            Icon="download-2-line"
                            tooltipKey={`${TRANSLATE_PREFIX}.ACTIONS.DOWNLOAD_TEMPLATE`}
                            className={classNames(css.iconButton, css.rounded)}
                            onClick={() => downloadAttributionRequestCsv(request, orgName)}
                        />
                    )}
                    {(isReadOnly || status === STATUSES.REJECTED) &&
                        isFormRequest &&
                        (adminMode && ADMIN_EDITABLE_STATUSES.includes(status) ? (
                            <Button
                                type="flat"
                                Icon="pencil"
                                tooltipKey="STATIC.BUTTONS.EDIT"
                                className={classNames(css.iconButton, css.rounded)}
                                onClick={() => navigateToRequest(request?.id)}
                            />
                        ) : (
                            <Button
                                disabled={disabled}
                                type="flat"
                                Icon="eye"
                                tooltipKey={`${TRANSLATE_PREFIX}.ACTIONS.VIEW`}
                                className={classNames(css.iconButton, css.rounded)}
                                onClick={() => navigateToRequest(request?.id)}
                            />
                        ))}
                    {hasHidden && (
                        <Button
                            type="flat"
                            Icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                            tooltipKey={`${TRANSLATE_PREFIX}.ACTIONS.${
                                isExpanded ? 'COLLAPSE_HISTORY' : 'VIEW_HISTORY'
                            }`}
                            className={classNames(css.iconButton, css.rounded)}
                            onClick={() => toggleExpanded(card.type)}
                        />
                    )}
                </div>
            ),
        });

        if (!hasHidden || !isExpanded) return [mainRow];

        const hiddenRows = hiddenRequests.map(hiddenReq =>
            buildRow(hiddenReq, {
                key: `${card.type}-hidden-${hiddenReq.id}`,
                isSubRow: true,
                trClass: css.hiddenRow,
                actions: (
                    <div className={css.actions} onClick={e => e.stopPropagation()}>
                        {isFormRequest && (
                            <Button
                                type="flat"
                                Icon="eye"
                                tooltipKey={`${TRANSLATE_PREFIX}.ACTIONS.VIEW`}
                                className={classNames(css.iconButton, css.rounded)}
                                onClick={() => navigateToRequest(hiddenReq.id)}
                            />
                        )}
                    </div>
                ),
            })
        );

        return [mainRow, ...hiddenRows];
    });

    return (
        <div className={css.tableScrollWrapper}>
            <Table
                metadata={tableMetadata}
                data={tableData}
                tableClass={css.integrationTable}
                thClass={css.tableHeaderCell}
                trClass={css.integrationRow}
                tbodyClass={css.integrationTableBody}
                zebraTable={false}
                rowHoverEnabled={false}
                showInnerBorder={false}
                roundTopCorners
                roundBottomCorners
            />
        </div>
    );
};

IntegrationTable.propTypes = {
    requests: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            request_type: PropTypes.string,
            status: PropTypes.string,
            updated_at: PropTypes.string,
        })
    ).isRequired,
    hiddenByType: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
    profileIncomplete: PropTypes.bool.isRequired,
    onRequestIntegration: PropTypes.func.isRequired,
    onDeleteClick: PropTypes.func.isRequired,
};

export default IntegrationTable;
